import React, { useState, useEffect } from 'react';

const AmortizationTable = ({
  currentDebt,
  updateCurrentDebt,
  formatCurrency,
  generateFullSchedule,
}) => {
  const [currentBalance, setCurrentBalance] = useState('');

  // Update the debt's fullSchedule if it's empty
  useEffect(() => {
    if (currentDebt && currentDebt.type !== 'credit-card' && (!currentDebt.fullSchedule || currentDebt.fullSchedule.length === 0)) {
      const schedule = generateFullSchedule(currentDebt.data, currentDebt.additionalPayments);
      updateCurrentDebt({ fullSchedule: schedule });
    }
  }, [currentDebt, generateFullSchedule, updateCurrentDebt]);

  // Only show for traditional loans
  if (!currentDebt || currentDebt.type === 'credit-card') {
    return null;
  }

  // Generate the current schedule based on debt data and additional payments
  const currentSchedule =
    currentDebt.fullSchedule && currentDebt.fullSchedule.length > 0
      ? currentDebt.fullSchedule
      : generateFullSchedule(currentDebt.data, currentDebt.additionalPayments);

  // Calculate current loan position based on first payment date
  const getCurrentPaymentPosition = () => {
    if (!currentDebt.data.first_payment_date) return 0;

    const today = new Date();
    const firstPaymentDate = new Date(currentDebt.data.first_payment_date);
    const monthsDiff = Math.max(
      0,
      (today.getFullYear() - firstPaymentDate.getFullYear()) * 12 +
        (today.getMonth() - firstPaymentDate.getMonth())
    );

    return Math.max(0, monthsDiff);
  };

  const currentPaymentPosition = getCurrentPaymentPosition();

  // Get current balance based on payment position
  const getCurrentBalance = () => {
    if (currentSchedule.length === 0) return currentDebt.data.principal || 0;

    const currentPayment = currentSchedule[currentPaymentPosition];
    return currentPayment ? currentPayment.remaining_balance : currentDebt.data.principal || 0;
  };

  const currentBalanceAmount = getCurrentBalance();

  // Handle additional payment input change
  const handleAdditionalPaymentChange = (paymentNumber, value) => {
    const numAmount = parseFloat(value) || 0;
    const newCustomPayments = { ...currentDebt.additionalPayments.custom_payments };

    if (numAmount > 0) {
      newCustomPayments[paymentNumber] = numAmount;
    } else {
      delete newCustomPayments[paymentNumber];
    }

    // Update additional payments
    const updatedAdditionalPayments = {
      ...currentDebt.additionalPayments,
      custom_payments: newCustomPayments,
    };

    // Regenerate schedule with new payments
    const newSchedule = generateFullSchedule(currentDebt.data, updatedAdditionalPayments);

    updateCurrentDebt({
      additionalPayments: updatedAdditionalPayments,
      fullSchedule: newSchedule,
    });
  };

  // Handle current balance override
  const handleCurrentBalanceUpdate = () => {
    const newBalance = parseFloat(currentBalance);
    if (isNaN(newBalance) || newBalance < 0) {
      alert('Please enter a valid current balance amount.');
      return;
    }

    // This is a simplified approach - in a real app you might want more sophisticated
    // calculation to determine what additional payments were made to reach this balance
    const originalBalance =
      currentSchedule[currentPaymentPosition]?.remaining_balance || currentDebt.data.principal;
    const difference = originalBalance - newBalance;

    if (difference > 0) {
      // Add the difference as an additional payment in the current payment period
      handleAdditionalPaymentChange(currentPaymentPosition + 1, difference);
    }

    setCurrentBalance('');
  };

  // Print amortization schedule
  const printAmortizationSchedule = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Amortization Schedule - ${currentDebt.data.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .loan-info { background: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
            .loan-info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
            th { background-color: #f8f9fa; font-weight: bold; text-align: center; }
            .text-left { text-align: left; }
            .text-center { text-align: center; }
            .interest { color: #dc3545; }
            .principal { color: #28a745; }
            .additional { color: #007bff; }
            .current-payment { background-color: #e3f2fd; font-weight: bold; }
            .past-payment { background-color: #f5f5f5; color: #666; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 Amortization Schedule</h1>
            <h2>${currentDebt.data.name}</h2>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="loan-info">
            <h3>Loan Information</h3>
            <div class="loan-info-grid">
              <div><strong>Principal Amount:</strong> $${formatCurrency(currentDebt.data.principal)}</div>
              <div><strong>Interest Rate:</strong> ${((currentDebt.data.annual_rate || 0) * 100).toFixed(2)}%</div>
              <div><strong>Loan Term:</strong> ${currentDebt.data.term_months} months</div>
              <div><strong>First Payment:</strong> ${currentDebt.data.first_payment_date}</div>
              <div><strong>Current Payment:</strong> #${currentPaymentPosition + 1}</div>
              <div><strong>Current Balance:</strong> $${formatCurrency(currentBalanceAmount)}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Payment</th>
                <th>Interest</th>
                <th>Principal</th>
                <th>Additional</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              ${currentSchedule
                .map((payment, index) => {
                  const isPastPayment = index < currentPaymentPosition;
                  const isCurrentPayment = index === currentPaymentPosition;
                  const additionalPayment =
                    currentDebt.additionalPayments.custom_payments[payment.payment_number] || 0;
                  const recurringExtra = currentDebt.additionalPayments.recurring_extra || 0;
                  const totalAdditional =
                    additionalPayment + (index > currentPaymentPosition ? recurringExtra : 0);

                  let rowClass = '';
                  if (isPastPayment) rowClass = 'past-payment';
                  else if (isCurrentPayment) rowClass = 'current-payment';

                  return `
                  <tr class="${rowClass}">
                    <td class="text-center">${payment.payment_number}${isCurrentPayment ? '<br><small>TODAY</small>' : ''}</td>
                    <td class="text-left">${payment.date}</td>
                    <td>$${formatCurrency(payment.payment_amount)}</td>
                    <td class="interest">$${formatCurrency(payment.interest)}</td>
                    <td class="principal">$${formatCurrency(payment.principal)}</td>
                    <td class="additional">${totalAdditional > 0 ? '$' + formatCurrency(totalAdditional) : '-'}</td>
                    <td>$${formatCurrency(payment.remaining_balance)}</td>
                  </tr>
                `;
                })
                .join('')}
            </tbody>
          </table>
          
          <div style="margin-top: 20px; font-size: 11px; color: #666;">
            <p><strong>Legend:</strong></p>
            <p>• Past payments are shown in gray (may include prior overpayments)</p>
            <p>• Current payment due is highlighted in blue</p>
            <p>• Future payments show planned additional principal amounts</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Render the payment row with appropriate styling based on status
  const renderPaymentRow = (payment, index) => {
    const isPastPayment = index < currentPaymentPosition;
    const isCurrentPayment = index === currentPaymentPosition;
    const isFuturePayment = index > currentPaymentPosition;

    let rowClasses = 'border-b border-border ';
    if (isPastPayment) {
      rowClasses += 'bg-surface-muted text-text-muted'; // Past payments
    } else if (isCurrentPayment) {
      rowClasses += 'bg-primary/20 text-text-primary font-medium'; // Current payment
    } else {
      rowClasses += 'bg-card text-text-primary'; // Future payments
    }

    const additionalPayment =
      currentDebt.additionalPayments.custom_payments[payment.payment_number] || 0;
    const recurringExtra = currentDebt.additionalPayments.recurring_extra || 0;

    return React.createElement(
      'tr',
      {
        key: payment.payment_number,
        className: rowClasses,
      },
      [
        // Payment Number
        React.createElement(
          'td',
          {
            key: 'payment-number',
            className: 'px-3 py-2 text-center',
          },
          [
            React.createElement('span', { key: 'number' }, payment.payment_number),
            isCurrentPayment &&
              React.createElement(
                'div',
                {
                  key: 'current-indicator',
                  className: 'text-xs text-primary font-bold',
                },
                'TODAY'
              ),
          ]
        ),

        // Date
        React.createElement(
          'td',
          {
            key: 'date',
            className: 'px-3 py-2',
          },
          payment.date
        ),

        // Payment Amount
        React.createElement(
          'td',
          {
            key: 'payment',
            className: 'px-3 py-2 text-right',
          },
          '$' + formatCurrency(payment.payment_amount)
        ),

        // Interest
        React.createElement(
          'td',
          {
            key: 'interest',
            className: 'px-3 py-2 text-right text-destructive',
          },
          '$' + formatCurrency(payment.interest)
        ),

        // Principal
        React.createElement(
          'td',
          {
            key: 'principal',
            className: 'px-3 py-2 text-right text-primary',
          },
          '$' + formatCurrency(payment.principal)
        ),

        // Additional Principal (Editable for all payments — record prior or planned overpayments)
        React.createElement(
          'td',
          {
            key: 'additional',
            className: 'px-3 py-2 text-right',
          },
          React.createElement('input', {
            type: 'number',
            step: '0.01',
            min: '0',
            value: additionalPayment > 0 ? additionalPayment : '',
            placeholder: isFuturePayment && recurringExtra > 0 ? '+' + formatCurrency(recurringExtra) : '0.00',
            onChange: (e) =>
              handleAdditionalPaymentChange(payment.payment_number, e.target.value),
            className:
              'w-20 px-2 py-1 text-sm border border-input rounded text-right bg-background text-foreground',
          })
        ),

        // Remaining Balance
        React.createElement(
          'td',
          {
            key: 'balance',
            className: 'px-3 py-2 text-right font-medium',
          },
          '$' + formatCurrency(payment.remaining_balance)
        ),
      ]
    );
  };

  return React.createElement(
    'div',
    {
      className: 'bg-card border border-border rounded-lg p-6 mt-8',
    },
    [
      // Header with print button only
      React.createElement(
        'div',
        {
          key: 'header',
          className: 'flex justify-between items-center mb-4',
        },
        [
          React.createElement(
            'h3',
            {
              key: 'title',
              className: 'text-xl font-semibold text-text-primary',
            },
            '📊 Interactive Amortization Schedule'
          ),

          React.createElement(
            'button',
            {
              key: 'print',
              onClick: printAmortizationSchedule,
              className:
                'px-4 py-2 bg-primary hover:opacity-90 text-primary-foreground rounded-md text-sm font-medium transition-opacity duration-200',
            },
            '🖨️ Print'
          ),
        ]
      ),

      // Current Balance Override Section
      React.createElement(
        'div',
        {
          key: 'current-balance-section',
          className: 'mb-6 p-4 bg-primary/10 border border-primary rounded-lg',
        },
        [
          React.createElement(
            'h4',
            {
              key: 'title',
              className: 'font-semibold text-primary mb-3',
            },
            '💡 Current Balance Override'
          ),

          React.createElement(
            'div',
            {
              key: 'description',
              className: 'text-sm text-text-primary mb-3',
            },
            'Based on your first payment date (' +
              currentDebt.data.first_payment_date +
              '), you should be on payment #' +
              (currentPaymentPosition + 1) +
              ' with a balance of $' +
              formatCurrency(currentBalanceAmount) +
              '. If your actual balance is different, enter it below:'
          ),

          React.createElement(
            'div',
            {
              key: 'input-section',
              className: 'flex gap-2 items-center',
            },
            [
              React.createElement('input', {
                key: 'balance-input',
                type: 'number',
                step: '0.01',
                min: '0',
                value: currentBalance,
                onChange: (e) => setCurrentBalance(e.target.value),
                placeholder: 'Current: $' + formatCurrency(currentBalanceAmount),
                className:
                  'px-3 py-2 border border-input rounded-md w-40 bg-background text-foreground',
              }),

              React.createElement(
                'button',
                {
                  key: 'update-button',
                  onClick: handleCurrentBalanceUpdate,
                  disabled: !currentBalance,
                  className:
                    'px-4 py-2 bg-primary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground rounded-md text-sm font-medium transition-opacity duration-200',
                },
                'Update Balance'
              ),
            ]
          ),
        ]
      ),

      // Legend
      React.createElement(
        'div',
        {
          key: 'legend',
          className: 'mb-4 p-3 bg-surface-muted rounded-lg border border-border',
        },
        [
          React.createElement(
            'div',
            {
              key: 'title',
              className: 'font-medium text-text-primary mb-2',
            },
            'Payment Status Legend:'
          ),

          React.createElement(
            'div',
            {
              key: 'legend-items',
              className: 'flex flex-wrap gap-4 text-sm',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'past',
                  className: 'flex items-center gap-2',
                },
                [
                  React.createElement('div', {
                    key: 'color',
                    className: 'w-4 h-4 bg-surface rounded border border-border',
                  }),
                  React.createElement(
                    'span',
                    { key: 'label', className: 'text-text-primary' },
                    'Past Payments (Editable for prior overpayments)'
                  ),
                ]
              ),

              React.createElement(
                'div',
                {
                  key: 'current',
                  className: 'flex items-center gap-2',
                },
                [
                  React.createElement('div', {
                    key: 'color',
                    className: 'w-4 h-4 bg-primary/30 rounded border border-primary',
                  }),
                  React.createElement(
                    'span',
                    { key: 'label', className: 'text-text-primary' },
                    'Current Payment Due'
                  ),
                ]
              ),

              React.createElement(
                'div',
                {
                  key: 'future',
                  className: 'flex items-center gap-2',
                },
                [
                  React.createElement('div', {
                    key: 'color',
                    className: 'w-4 h-4 bg-card rounded border border-border',
                  }),
                  React.createElement(
                    'span',
                    { key: 'label', className: 'text-text-primary' },
                    'Future Payments (Editable)'
                  ),
                ]
              ),
            ]
          ),
        ]
      ),

      // Amortization Table
      currentSchedule.length > 0 &&
        React.createElement(
          'div',
          {
            key: 'table-container',
            className: 'max-h-96 overflow-y-auto border border-border rounded-lg',
          },
          [
            React.createElement(
              'table',
              {
                key: 'table',
                className: 'w-full text-sm',
              },
              [
                // Table Header
                React.createElement(
                  'thead',
                  {
                    key: 'header',
                    className: 'bg-surface-muted sticky top-0',
                  },
                  [
                    React.createElement('tr', { key: 'header-row' }, [
                      React.createElement(
                        'th',
                        {
                          key: 'payment-num',
                          className: 'px-3 py-2 text-left text-text-primary font-medium',
                        },
                        '#'
                      ),
                      React.createElement(
                        'th',
                        {
                          key: 'date',
                          className: 'px-3 py-2 text-left text-text-primary font-medium',
                        },
                        'Date'
                      ),
                      React.createElement(
                        'th',
                        {
                          key: 'payment',
                          className: 'px-3 py-2 text-right text-text-primary font-medium',
                        },
                        'Payment'
                      ),
                      React.createElement(
                        'th',
                        {
                          key: 'interest',
                          className: 'px-3 py-2 text-right text-text-primary font-medium',
                        },
                        'Interest'
                      ),
                      React.createElement(
                        'th',
                        {
                          key: 'principal',
                          className: 'px-3 py-2 text-right text-text-primary font-medium',
                        },
                        'Principal'
                      ),
                      React.createElement(
                        'th',
                        {
                          key: 'additional',
                          className: 'px-3 py-2 text-right text-text-primary font-medium',
                        },
                        'Additional Principal'
                      ),
                      React.createElement(
                        'th',
                        {
                          key: 'balance',
                          className: 'px-3 py-2 text-right text-text-primary font-medium',
                        },
                        'Remaining Balance'
                      ),
                    ]),
                  ]
                ),

                // Table Body
                React.createElement(
                  'tbody',
                  {
                    key: 'body',
                  },
                  currentSchedule.map((payment, index) => renderPaymentRow(payment, index))
                ),
              ]
            ),
          ]
        ),

      // Summary when table is visible
      currentSchedule.length > 0 &&
        React.createElement(
          'div',
          {
            key: 'summary',
            className: 'mt-4 p-4 bg-surface-muted rounded-lg border border-border',
          },
          [
            React.createElement(
              'div',
              {
                key: 'summary-title',
                className: 'font-medium text-text-primary mb-2',
              },
              'Schedule Summary:'
            ),

            React.createElement(
              'div',
              {
                key: 'summary-items',
                className: 'grid grid-cols-2 md:grid-cols-4 gap-4 text-sm',
              },
              [
                React.createElement('div', { key: 'total-payments' }, [
                  React.createElement(
                    'span',
                    {
                      key: 'label',
                      className: 'text-text-primary',
                    },
                    'Total Payments: '
                  ),
                  React.createElement(
                    'span',
                    {
                      key: 'value',
                      className: 'font-semibold text-text-primary',
                    },
                    currentSchedule.length
                  ),
                ]),

                React.createElement('div', { key: 'current-position' }, [
                  React.createElement(
                    'span',
                    {
                      key: 'label',
                      className: 'text-text-primary',
                    },
                    'Current Position: '
                  ),
                  React.createElement(
                    'span',
                    {
                      key: 'value',
                      className: 'font-semibold text-text-primary',
                    },
                    `Payment ${currentPaymentPosition + 1}`
                  ),
                ]),

                React.createElement('div', { key: 'remaining-balance' }, [
                  React.createElement(
                    'span',
                    {
                      key: 'label',
                      className: 'text-text-primary',
                    },
                    'Current Balance: '
                  ),
                  React.createElement(
                    'span',
                    {
                      key: 'value',
                      className: 'font-semibold text-primary',
                    },
                    `$${formatCurrency(currentBalanceAmount)}`
                  ),
                ]),

                React.createElement('div', { key: 'payments-remaining' }, [
                  React.createElement(
                    'span',
                    {
                      key: 'label',
                      className: 'text-text-primary',
                    },
                    'Payments Remaining: '
                  ),
                  React.createElement(
                    'span',
                    {
                      key: 'value',
                      className: 'font-semibold text-text-primary',
                    },
                    Math.max(0, currentSchedule.length - currentPaymentPosition - 1)
                  ),
                ]),
              ]
            ),
          ]
        ),
    ]
  );
};

export default AmortizationTable;
